import { useCallback, useMemo } from "react";
import { Box, Typography, Tooltip, IconButton, Badge, Chip, LinearProgress, alpha, useTheme } from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import DescriptionIcon from "@mui/icons-material/Description";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import ImageIcon from "@mui/icons-material/Image";
import { useI18n } from "../../../../hooks/useI18n";

const IMAGE_EXTENSIONS = ["png", "jpeg", "jpg", "tiff"];

interface FileUploadZoneFileAttachmentProps {
  file: File,
  onDelete: () => void;
  progress?: number;
  loading?: boolean;
  isValid?: boolean;
  errorMessage?: string;
}

const formatFileSize = (size: number): string => {
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

export function FileUploadZoneFileAttachment({
  file,
  progress = 0,
  loading = false,
  isValid = true,
  errorMessage,
  onDelete
}: FileUploadZoneFileAttachmentProps) {
  const theme = useTheme();
  const { t } = useI18n();

  const fileIsInvalid = isValid === false;

  const getFileIcon = useCallback(
    (name: string) => {
      const extension = name.split(".").pop()?.toLowerCase() || "";

      if (extension === "pdf") {
        return <DescriptionIcon fontSize="small" sx={{ color: theme.palette.primary.main }} />;
      } else if (IMAGE_EXTENSIONS.includes(extension)) {
        return <ImageIcon fontSize="small" sx={{ color: theme.palette.primary.main }} />;
      } else {
        return <InsertDriveFileIcon fontSize="small" sx={{ color: theme.palette.grey[600] }} />;
      }
    },
    [theme],
  );

  const fileStatus = useMemo(() => {
    if (loading) {
      if (progress === 0) return "waiting";
      if (progress > 0 && progress < 100) return "uploading";
      if (progress >= 100) return "completed";
    }
    return "idle";
  }, [loading, progress]);

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        p: 1.5,
        position: "relative",
        borderBottom: `1px solid ${theme.palette.divider}`,
        backgroundColor: fileIsInvalid
          ? alpha(theme.palette.error.light, 0.07)
          : fileStatus === "completed"
            ? alpha(theme.palette.success.light, 0.05)
            : undefined,
        "&:hover": {
          backgroundColor: fileIsInvalid
            ? alpha(theme.palette.error.light, 0.12)
            : fileStatus === "completed"
              ? alpha(theme.palette.success.light, 0.1)
              : alpha(theme.palette.primary.light, 0.05),
        },
        transition: theme.transitions.create(["background-color"]),
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          overflow: "hidden",
          flex: 1,
        }}
      >
        <Box sx={{ mr: 1.5, display: "flex", alignItems: "center" }}>
          {fileIsInvalid ? (
            <Badge
              overlap="circular"
              badgeContent={
                <ErrorOutlineIcon fontSize="small" sx={{ fontSize: 14, color: theme.palette.error.light }} />
              }
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "right",
              }}
            >
              {getFileIcon(file.name)}
            </Badge>
          ) : (
            <Badge
              overlap="circular"
              badgeContent={
                fileStatus === "completed" ? (
                  <CheckCircleOutlineIcon fontSize="small" sx={{ fontSize: 14, color: theme.palette.success.main }} />
                ) : null
              }
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "right",
              }}
            >
              {getFileIcon(file.name)}
            </Badge>
          )}
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "start",
            overflow: "hidden",
            flex: 1,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
            <Tooltip title={file.name}>
              <Typography
                variant="body2"
                sx={{
                  textAlign: "left",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  width: "100%",
                  fontWeight: 500,
                  color: fileIsInvalid
                    ? theme.palette.error.light
                    : undefined,
                }}
              >
                {file.name}
              </Typography>
            </Tooltip>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", mt: 0.5, width: "100%" }}>
            <Typography variant="caption" color="text.secondary" sx={{ minHeight: "1.2em" }}>
              {progress > 0 && progress < 100
                ? t("documents.file_upload.uploading", {
                  percent: Math.round(progress)
                })
                : file.size !== undefined
                  ? formatFileSize(file.size)
                  : null}
            </Typography>
            {fileStatus === "waiting" && (
              <Chip
                icon={<HourglassEmptyIcon fontSize="small" />}
                label={t("documents.file_upload.in_queue")}
                size="medium"
                sx={{
                  ml: 1,
                  height: 20,
                  fontSize: "0.65rem",
                  backgroundColor: alpha(theme.palette.warning.light, 0.1),
                  color: theme.palette.warning.dark,
                  "& .MuiChip-icon": {
                    fontSize: "0.8rem",
                    color: theme.palette.warning.main,
                  },
                  borderRadius: "4px",
                }}
              />
            )}
            {fileStatus === "completed" && (
              <Chip
                icon={<CheckCircleOutlineIcon fontSize="small" />}
                label={t("documents.file_upload.uploaded")}
                size="medium"
                sx={{
                  ml: 1,
                  height: 20,
                  fontSize: "0.65rem",
                  backgroundColor: alpha(theme.palette.success.light, 0.1),
                  color: theme.palette.success.dark,
                  "& .MuiChip-icon": {
                    fontSize: "0.8rem",
                    color: theme.palette.success.main,
                  },
                  borderRadius: "4px",
                }}
              />
            )}
            {fileIsInvalid && (
              <Tooltip title={errorMessage || ""}>
                <Typography
                  variant="caption"
                  sx={{
                    ml: 1,
                    color: theme.palette.error.light,
                    fontWeight: 500,
                  }}
                >
                  {errorMessage}
                </Typography>
              </Tooltip>
            )}
          </Box>
        </Box>
      </Box>

      {fileStatus === "uploading" && (
        <Box sx={{ position: "absolute", bottom: 6, left: 0, right: 0, px: 1.5 }}>
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              height: 4,
              borderRadius: 2,
              backgroundColor: alpha(theme.palette.primary.main, 0.1),
              "& .MuiLinearProgress-bar": {
                borderRadius: 2,
              },
            }}
          />
        </Box>
      )}

      <Tooltip title={t("common.cta.delete")}>
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          disabled={loading}
          sx={{
            color: theme.palette.text.secondary,
            opacity: loading ? 0.5 : 1,
            "&:hover": {
              opacity: 1,
              backgroundColor: alpha(theme.palette.error.light, 0.15),
              color: theme.palette.error.main,
            },
          }}
        >
          <DeleteOutlineIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );
}
